import { useEffect, useState } from "react";
import { lpTrack } from "./lpTrack";

/**
 * Client helpers shared by the AXA paid-LP components (hero, promo bar, FAB,
 * section CTAs). All SSR-safe. Kept page-scoped — nothing here is imported by
 * shared/site-wide components, so other pages are unaffected.
 */

// id of the hero calculator card — the smooth-scroll target + pulse anchor.
export const FORM_CARD_ID = "paid-hero-form";
const SCROLL_OFFSET = 90; // clears the sticky navbar

/**
 * Smooth-scroll to the hero calculator and fire a brief focus pulse on it.
 * Emits a cta_click analytics event (same dataLayer push the LP already uses).
 * Use as an onClick on the "Calcular…" anchors; keep the href as a no-JS
 * fallback (the calculator anchor `#calculadora-life5` still works natively).
 */
export const scrollToForm = (location?: string): void => {
  if (location) lpTrack("cta_click", { location });
  if (typeof window === "undefined") return;
  const el = document.getElementById(FORM_CARD_ID);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
  window.scrollTo({ top, behavior: "smooth" });
  el.classList.add("axa-pulse");
  window.setTimeout(() => el.classList.remove("axa-pulse"), 1300);
};

/**
 * True once the page has scrolled past `threshold` px. Drives the FAB (appears)
 * and the navbar phone link (hides). Uses requestAnimationFrame so the scroll
 * listener stays cheap. Returns false during SSR / first paint.
 */
export const useScrolled = (threshold = 80): boolean => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setScrolled(window.scrollY > threshold);
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
};
